import React, { cloneElement, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { Icon } from "@/components/ui/display/Icon";
import { usePortalPosition, useDismissableHint } from "@/hooks/ui";
import type {
  PointingHintProps,
  AnchoredPointingHintProps,
  PointingDirection,
} from "./types";
import styles from "./PointingHint.module.css";

/** Duration of the fade transition in milliseconds */
const FADE_DURATION_MS = 350;

/**
 * Maps direction to the appropriate arrow icon name.
 * Direction = where hint is positioned relative to target.
 * Arrow points TOWARD the target (opposite direction).
 */
const directionToArrow: Record<PointingDirection, string> = {
  left: "arrowRight", // hint is left, arrow points right →
  right: "arrowLeft", // hint is right, arrow points left ←
  "top-left": "arrowBottomRight", // hint is top-left, arrow points ↘
  "top-center": "arrowDown", // hint is above, arrow points down ↓
  "top-right": "arrowBottomLeft", // hint is top-right, arrow points ↙
  "bottom-left": "arrowTopRight", // hint is bottom-left, arrow points ↗
  "bottom-center": "arrowUp", // hint is below, arrow points up ↑
  "bottom-right": "arrowTopLeft", // hint is bottom-right, arrow points ↖
};

/**
 * PointingHint Component
 *
 * Displays an animated hint with a bouncing arrow pointing in a specified direction.
 * Use to draw attention to UI elements or provide contextual guidance.
 *
 * @example
 * ```tsx
 * // Standalone hint
 * <PointingHint direction="left">
 *   Select a project to get started
 * </PointingHint>
 *
 * // Anchored to an element (like a tooltip)
 * <PointingHint.Anchored
 *   direction="left"
 *   target={<Button>Click me</Button>}
 * >
 *   Click here to continue
 * </PointingHint.Anchored>
 * ```
 */
export const PointingHint: React.FC<PointingHintProps> & {
  Anchored: React.FC<AnchoredPointingHintProps>;
} = ({
  children,
  direction = "left",
  visible = true,
  animated = true,
  animationDuration = 1.2,
  variant = "primary",
  className,
  "data-testid": testId,
}) => {
  if (!visible) {
    return null;
  }

  const arrowIcon = directionToArrow[direction];

  return (
    <div
      className={classNames(styles.hint, className)}
      data-direction={direction}
      data-variant={variant}
      data-animated={animated ? "true" : "false"}
      style={
        {
          "--animation-duration": `${animationDuration}s`,
        } as React.CSSProperties
      }
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      <Icon
        name={arrowIcon}
        size="lg"
        data-element="arrow"
        aria-hidden={true}
      />
      <span data-element="text">{children}</span>
    </div>
  );
};

/**
 * AnchoredPointingHint Component
 *
 * A pointing hint that anchors to a target element using a portal.
 * Renders outside the DOM hierarchy to avoid z-index issues.
 * The hint appears adjacent to the target element with the arrow pointing at it.
 *
 * @example
 * ```tsx
 * // Dismissable hint - stays hidden after user performs the action
 * <PointingHint.Anchored
 *   direction="right"
 *   storageKey="floorplan-rotation"
 *   target={<RotationButton />}
 * >
 *   Try rotating the floor plan!
 * </PointingHint.Anchored>
 * ```
 */
const AnchoredPointingHint: React.FC<AnchoredPointingHintProps> = ({
  target,
  children,
  direction = "left",
  visible = true,
  animated = true,
  animationDuration = 1.2,
  variant = "primary",
  offset = 8,
  storageKey,
  onDismiss,
  noFadeOut = false,
  className,
  "data-testid": testId,
}) => {
  const arrowIcon = directionToArrow[direction];

  // Handle dismissable persistence
  const { isDismissed, dismiss } = useDismissableHint(storageKey);

  // Compute effective visibility: visible prop AND not dismissed
  const isVisible = visible && !isDismissed;

  // Track mounted state for fade-out animation
  // shouldRender keeps element mounted during fade-out
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Becoming visible: render immediately, CSS handles fade-in
      setShouldRender(true);
      setIsFadingOut(false);
    } else if (shouldRender) {
      // Becoming hidden: either fade-out or unmount immediately
      if (noFadeOut) {
        // Unmount immediately without fade animation
        setShouldRender(false);
      } else {
        // Start fade-out, then unmount after animation
        setIsFadingOut(true);
        const timer = setTimeout(() => {
          setShouldRender(false);
          setIsFadingOut(false);
        }, FADE_DURATION_MS);
        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, shouldRender, noFadeOut]);

  // Use shared hook for portal positioning
  const {
    triggerRef: targetRef,
    elementRef: hintRef,
    coords,
  } = usePortalPosition({
    position: direction,
    offset,
    isVisible: shouldRender,
  });

  // Wrap target's onClick to dismiss hint when storageKey is provided
  const targetProps = target.props as {
    onClick?: (e: React.MouseEvent) => void;
  };
  const wrappedTarget = storageKey
    ? cloneElement(
        target as React.ReactElement<{
          onClick?: (e: React.MouseEvent) => void;
        }>,
        {
          onClick: (e: React.MouseEvent) => {
            // Call original onClick if it exists
            targetProps.onClick?.(e);
            // Dismiss the hint
            dismiss();
            onDismiss?.();
          },
        },
      )
    : target;

  return (
    <>
      <div ref={targetRef} className={styles.targetWrapper}>
        {wrappedTarget}
      </div>
      {shouldRender &&
        createPortal(
          <div
            ref={hintRef}
            className={classNames(styles.hint, styles.portalHint, className)}
            data-direction={direction}
            data-variant={variant}
            data-animated={animated ? "true" : "false"}
            data-fading={isFadingOut ? "out" : "in"}
            style={
              {
                "--animation-duration": `${animationDuration}s`,
                "--fade-duration": `${FADE_DURATION_MS}ms`,
                position: "absolute",
                top: coords?.top ?? -9999,
                left: coords?.left ?? -9999,
                zIndex: 9999,
              } as React.CSSProperties
            }
            data-testid={testId}
            role="status"
            aria-live="polite"
          >
            <Icon
              name={arrowIcon}
              size="lg"
              data-element="arrow"
              aria-hidden={true}
            />
            <span data-element="text">{children}</span>
          </div>,
          document.body,
        )}
    </>
  );
};

// Attach Anchored as a static property
PointingHint.Anchored = AnchoredPointingHint;

PointingHint.displayName = "PointingHint";
AnchoredPointingHint.displayName = "PointingHint.Anchored";

export default PointingHint;
